/** @file DFS interleaving of bone and IK ops (§6.6): ancestors first, descendants recomputed after a solve. */
import type { Pose2DJson } from '@pose2d/format/types';
import { inBoneSubtree } from './inBoneSubtree';
import type { IkChain } from './sortIkChains';

type Ops = Pose2DJson['updateOrder'];

/** Emits `['b', i]` for `bone` and every not-yet-computed ancestor, root first (no-op for -1). */
function visit(ops: Ops, done: Uint8Array, parents: readonly number[], bone: number): void {
  const path: number[] = [];
  for (let b = bone; b >= 0 && done[b] === 0; b = parents[b] ?? -1) path.push(b);
  for (let k = path.length - 1; k >= 0; k--) {
    const b = path[k] ?? 0;
    ops.push(['b', b]);
    done[b] = 1;
  }
}

/** Each chain after its target and base bone; chain bones are the IK op's; every other bone once after its parent. */
export function interleaveUpdateOps(parents: readonly number[], chains: readonly IkChain[]): Ops {
  const ops: Ops = [];
  const done = new Uint8Array(parents.length);
  for (const c of chains) {
    visit(ops, done, parents, c.target);
    visit(ops, done, parents, parents[c.bones[0] ?? -1] ?? -1);
    ops.push(['ik', c.ik]);
    for (const b of c.bones) done[b] = 1;
    for (let b = 0; b < parents.length; b++) {
      if (!c.bones.includes(b) && inBoneSubtree(parents, b, c.bones)) done[b] = 0;
    }
  }
  for (let b = 0; b < parents.length; b++) visit(ops, done, parents, b);
  return ops;
}
